import { motion } from "motion/react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { api } from "../../../lib/api";
import { formatInr, stripCurrencySymbolPrefix } from "../../../lib/currency";
import { useAuth } from "../../../providers/AuthProvider";
import { DASHBOARD_SIDE_CARD_LIST_HEIGHT_CLASS } from "./dashboardFeedConstants";
import type { ActivityFeedItem } from "./mapRecentActivityFeed";
import { ActivityFeedRow } from "./ActivityFeedRow";

const MAX_DASHBOARD_ROWS = 5;

export function RecentActivity({
  feed,
  viewAllHref = "/recent-activity",
}: {
  feed?: ActivityFeedItem[];
  /** Business owner default: `/recent-activity`; super admin: `/admin/recent-activity` */
  viewAllHref?: string;
}) {
  const { token } = useAuth();
  const [activities, setActivities] = useState<ActivityFeedItem[]>([]);

  const useDashboardFeed = feed !== undefined;

  const relativeTime = useMemo(() => {
    const fmt = (msAgo: number) => {
      const s = Math.floor(msAgo / 1000);
      const m = Math.floor(s / 60);
      const h = Math.floor(m / 60);
      const d = Math.floor(h / 24);

      if (s < 60) return `${s} seconds ago`;
      if (m < 60) return `${m} minutes ago`;
      if (h < 24) return `${h} hours ago`;
      return `${d} days ago`;
    };

    return (iso: string | null | undefined) => {
      if (!iso) return "—";
      const t = new Date(iso).getTime();
      if (!Number.isFinite(t)) return "—";
      return fmt(Date.now() - t);
    };
  }, []);

  useEffect(() => {
    if (useDashboardFeed) {
      setActivities((feed ?? []).slice(0, MAX_DASHBOARD_ROWS));
    }
  }, [feed, useDashboardFeed]);

  useEffect(() => {
    if (useDashboardFeed) return;
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.reports.getRecentOrders({ token, limit: MAX_DASHBOARD_ROWS });
        if (cancelled) return;

        const rows = (res.data as any[]) ?? [];
        setActivities(
          rows.slice(0, MAX_DASHBOARD_ROWS).map((r, idx) => {
            const orderType = String(r.order_type ?? "");
            const createdAt = r.created_at ? String(r.created_at) : null;
            const amountNum = Number(r.amount ?? 0);
            const amount = Number.isFinite(amountNum) ? formatInr(amountNum) : undefined;

            if (orderType === "PO") {
              return {
                id: `act_${idx}_${r.order_id ?? ""}`,
                type: "order" as const,
                title: `Purchase Order - ${r.order_id ?? ""}`,
                description: `Supplier: ${r.customer ?? "-"}`,
                time: relativeTime(createdAt),
                amount,
              } satisfies ActivityFeedItem;
            }

            return {
              id: `act_${idx}_${r.order_id ?? ""}`,
              type: "sale" as const,
              title: `New Sale - ${r.order_id ?? ""}`,
              description: `Customer: ${r.customer ?? "-"}`,
              time: relativeTime(createdAt),
              amount,
            } satisfies ActivityFeedItem;
          }),
        );
      } catch {
        // ignore - keep empty
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, relativeTime, useDashboardFeed]);

  const displayed = activities.slice(0, MAX_DASHBOARD_ROWS);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.4 }}
    >
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle>Recent Activity</CardTitle>
          <Button variant="ghost" size="sm" className="text-primary" asChild>
            <Link to={viewAllHref}>
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          <div
            className={`${DASHBOARD_SIDE_CARD_LIST_HEIGHT_CLASS} overflow-y-auto space-y-3 pr-1 -mr-1`}
          >
            {displayed.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-sm px-4 text-center">
                No recent activity yet.
              </div>
            ) : (
              displayed.map((activity, index) => (
                <ActivityFeedRow key={activity.id} activity={activity} index={index} delay={0.1} />
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
